import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { dispatchSweetAlert } from '../../../actions'
import branditRails from '../../../api/brandit_rails'

// Load AcceptUI.js library for hosted payment form
const loadAcceptUIScript = paymentGatewayConfig => {
  return new Promise((resolve, reject) => {
    // Check if AcceptUI is already loaded
    if (window.AcceptUI) {
      resolve(window.AcceptUI)
      return
    }

    // Check if Accept is loaded (fallback)
    if (window.Accept) {
      resolve(window.Accept)
      return
    }

    const isProdEnvironment = paymentGatewayConfig?.environment === 'production'
    const acceptUIScriptUrl = isProdEnvironment
      ? 'https://js.authorize.net/v1/Accept.js'
      : 'https://jstest.authorize.net/v1/Accept.js'
    const fallbackAcceptScriptUrl = isProdEnvironment
      ? 'https://js.authorize.net/v3/Accept.js'
      : 'https://jstest.authorize.net/v3/Accept.js'

    // Load AcceptUI.js first - use production or test based on environment
    const acceptUIScript = document.createElement('script')
    acceptUIScript.src = acceptUIScriptUrl
    acceptUIScript.charset = 'utf-8'

    acceptUIScript.onload = () => {
      if (window.AcceptUI) {
        resolve(window.AcceptUI)
      } else if (window.Accept) {
        resolve(window.Accept)
      } else {
        // If AcceptUI.js didn't work, try loading Accept.js v3 directly
        const acceptScript = document.createElement('script')
        acceptScript.src = fallbackAcceptScriptUrl
        acceptScript.charset = 'utf-8'

        acceptScript.onload = () => {
          if (window.Accept) {
            resolve(window.Accept)
          } else {
            reject(new Error('Neither AcceptUI.js nor Accept.js could be loaded'))
          }
        }

        acceptScript.onerror = () => {
          reject(new Error('Failed to load Accept.js'))
        }
        document.head.appendChild(acceptScript)
      }
    }

    acceptUIScript.onerror = () => {
      // If AcceptUI.js fails, try Accept.js v3 directly
      const acceptScript = document.createElement('script')
      acceptScript.src = fallbackAcceptScriptUrl
      acceptScript.charset = 'utf-8'

      acceptScript.onload = () => {
        if (window.Accept) {
          resolve(window.Accept)
        } else {
          reject(new Error('Accept.js could not be loaded'))
        }
      }

      acceptScript.onerror = () => {
        reject(new Error('Failed to load Accept.js'))
      }
      document.head.appendChild(acceptScript)
    }

    document.head.appendChild(acceptUIScript)
  })
}

const AuthorizeNetCheckoutUIModal = ({
  amount,
  currentUser,
  paymentGatewayConfig,
  orderData,
  checkoutSessionToken,
  renderFormOnly, // ✅ Add this
  hideModal, // ✅ Already added
  onPaymentSuccess,
  onPaymentError,
  setPaymentMethod,
  onCardDataChange,
  dispatchSweetAlert
  // OPTION A: New props needed for full order data
  // orderData: PropTypes.object.isRequired, // Full order data matching /orders/validate structure
  // youPay: PropTypes.number // Amount to pay (already exists but documenting)
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentData, setPaymentData] = useState({
    dataValue: '',
    dataDescriptor: ''
  })
  const [acceptUI, setAcceptUI] = useState(null)
  const [wasCancelled, setWasCancelled] = useState(false) // ✅ Add this flag

  // Initialize AcceptUI.js when component mounts
  useEffect(() => {
    loadAcceptUIScript(paymentGatewayConfig)
      .then(acceptObject => {
        setAcceptUI(acceptObject)
      })
      .catch(error => {
        // Script loading failed
      })
  }, [paymentGatewayConfig])

  // Auto-trigger payment form when renderFormOnly is true
  useEffect(() => {
    if (renderFormOnly && acceptUI && !isProcessing && !wasCancelled) {
      handleHostedPayment()
    }
  }, [renderFormOnly, acceptUI, isProcessing, wasCancelled])

  const handleHostedPayment = async () => {
    setIsProcessing(true)

    try {
      if (!acceptUI) {
        throw new Error('Payment form library not loaded. Please try again.')
      }

      // Configure the Authorize.net hosted payment form
      const authData = {
        clientKey: paymentGatewayConfig?.config?.public_client_key,
        apiLoginID: paymentGatewayConfig?.config?.api_login_id
      }

      // Use AcceptUI.js Authorize.net hosted payment form
      if (acceptUI.hostedPay) {
        acceptUI.hostedPay.setup({
          authData,
          responseHandler(response) {
            if (response.messages && response.messages.resultCode === 'Error') {
              dispatchSweetAlert({
                alertMessage: `Payment form error 1: ${response.messages.message[0].text}`,
                showCancel: false
              })
            } else if (response.opaqueData) {
              // Store the real payment nonce from Authorize.net
              setPaymentData({
                dataValue: response.opaqueData.dataValue,
                dataDescriptor: response.opaqueData.dataDescriptor
              })

              // Update the payment method to indicate hosted form was used
              if (setPaymentMethod) {
                setPaymentMethod('hosted_credit_card_ui')
              }

              // Process the payment with the real nonce
              processPaymentWithNonce(response.opaqueData)
            } else {
              dispatchSweetAlert({
                alertMessage: 'Unexpected response from payment processor',
                showCancel: false
              })
            }
          }
        })

        // Open the hosted payment form
        acceptUI.hostedPay.open()
      } else {
        // Fallback: Use Accept.js dispatchData method
        // Create a modal with a form that will use Accept.js
        const modal = document.createElement('div')
        modal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 3000;
        `
        // z-index: 3000; necessary to override the confirmation modal z-index

        const form = document.createElement('form')
        form.id = 'hostedPaymentForm'
        form.style.cssText = `
          background: white;
          padding: 30px;
          border-radius: 8px;
          width: 400px;
          max-width: 90%;
        `

        const title = document.createElement('h4')
        title.textContent = 'Secure Payment Form'
        title.style.marginBottom = '20px'
        form.appendChild(title)

        // Add instruction text
        const instruction = document.createElement('p')
        instruction.textContent = 'Please enter your card number without spaces or dashes.'
        instruction.style.fontSize = '14px'
        instruction.style.color = '#666'
        instruction.style.marginBottom = '20px'
        instruction.style.marginTop = '0'
        form.appendChild(instruction)

        // Card Number Input
        const cardNumberInput = document.createElement('input')
        cardNumberInput.type = 'text'
        cardNumberInput.name = 'cardNumber'
        cardNumberInput.id = 'hostedCardNumber'
        cardNumberInput.placeholder = 'Card Number'
        cardNumberInput.style.cssText =
          'width: 100%; padding: 8px; margin: 5px 0; border: 1px solid #ccc; border-radius: 4px;'

        // Expiration Month Input
        const expMonthInput = document.createElement('input')
        expMonthInput.type = 'text'
        expMonthInput.name = 'expMonth'
        expMonthInput.id = 'hostedExpMonth'
        expMonthInput.placeholder = 'MM'
        expMonthInput.style.cssText =
          'width: 45%; padding: 8px; margin: 5px 5px 5px 0; border: 1px solid #ccc; border-radius: 4px;'

        // Expiration Year Input
        const expYearInput = document.createElement('input')
        expYearInput.type = 'text'
        expYearInput.name = 'expYear'
        expYearInput.id = 'hostedExpYear'
        expYearInput.placeholder = 'YY'
        expYearInput.style.cssText =
          'width: 45%; padding: 8px; margin: 5px 0 5px 5px; border: 1px solid #ccc; border-radius: 4px;'

        // Card Code Input
        const cardCodeInput = document.createElement('input')
        cardCodeInput.type = 'text'
        cardCodeInput.name = 'cardCode'
        cardCodeInput.id = 'hostedCardCode'
        cardCodeInput.placeholder = 'CVC'
        cardCodeInput.style.cssText =
          'width: 100%; padding: 8px; margin: 5px 0; border: 1px solid #ccc; border-radius: 4px;'

        // Submit button
        const submitButton = document.createElement('button')
        submitButton.type = 'button'
        submitButton.textContent = `Pay $${(amount / 100).toFixed(2)}`
        submitButton.style.cssText =
          'width: 100%; padding: 12px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; margin-top: 15px;'
        submitButton.onclick = function () {
          const cardData = {
            cardNumber: document.getElementById('hostedCardNumber').value,
            month: document.getElementById('hostedExpMonth').value,
            year: document.getElementById('hostedExpYear').value,
            cardCode: document.getElementById('hostedCardCode').value
          }

          // Validate required fields before submitting
          if (!cardData.cardNumber || cardData.cardNumber.trim() === '') {
            dispatchSweetAlert({
              alertMessage: 'Please provide a valid card number.',
              showCancel: false
            })
            return
          }

          if (!cardData.month || cardData.month.trim() === '') {
            dispatchSweetAlert({
              alertMessage: 'Please provide a valid expiration month.',
              showCancel: false
            })
            return
          }

          if (!cardData.year || cardData.year.trim() === '') {
            dispatchSweetAlert({
              alertMessage: 'Please provide a valid expiration year.',
              showCancel: false
            })
            return
          }

          if (!cardData.cardCode || cardData.cardCode.trim() === '') {
            dispatchSweetAlert({
              alertMessage: 'Please provide a valid CVC/CVV code.',
              showCancel: false
            })
            return
          }

          // Validate CVC format (3-4 digits)
          const cvcPattern = /^\d{3,4}$/
          if (!cvcPattern.test(cardData.cardCode.trim())) {
            dispatchSweetAlert({
              alertMessage: 'CVC/CVV must be 3 or 4 digits.',
              showCancel: false
            })
            return
          }

          // Prevent double-clicks while processing
          if (!submitButton.disabled) {
            submitButton.disabled = true
            submitButton.textContent = 'Processing...'
            submitButton.style.cursor = 'not-allowed'
          }

          // Use Accept.js dispatchData to get real payment nonce
          if (window.Accept && window.Accept.dispatchData) {
            // Try different approaches to call dispatchData (same as inline form)
            try {
              // Method 1: Try with authData first
              // Add timeout to catch when Authorize.net doesn't respond
              // This will only fire if NO response is received (success or error)
              let responseReceived = false
              const timeoutId = setTimeout(() => {
                if (!responseReceived) {
                  // Payment validation timeout
                }
              }, 10000) // 10 second timeout

              window.Accept.dispatchData(authData, cardData, function (response) {
                responseReceived = true
                clearTimeout(timeoutId) // Clear timeout for any response (success or error)

                if (response.opaqueData) {
                  // Store the real payment nonce from Authorize.net
                  setPaymentData({
                    dataValue: response.opaqueData.dataValue,
                    dataDescriptor: response.opaqueData.dataDescriptor
                  })

                  // Update the payment method to indicate hosted form was used
                  if (setPaymentMethod) {
                    setPaymentMethod('hosted_credit_card_ui')
                  }

                  // Process the payment with the real nonce
                  processPaymentWithNonce(response.opaqueData)

                  // Close modal
                  document.body.removeChild(modal)
                } else {
                  // Check if response has a different structure
                  if (response.messages && response.messages.resultCode === 'Error') {
                    const errorMessage = response.messages.message?.[0]?.text || 'Unknown error'

                    // Enhanced error handling for common Authorize.net issues
                    let userFriendlyMessage = 'Payment failed. Please try again.'

                    if (errorMessage.includes('cardNumber')) {
                      if (errorMessage.includes('MaxLength')) {
                        userFriendlyMessage =
                          'Invalid card number format. Please enter your card number without spaces or dashes.'
                      } else if (errorMessage.includes('cardNumber')) {
                        userFriendlyMessage = 'Please check your card number and try again.'
                      }
                    } else if (errorMessage.includes('expirationDate')) {
                      userFriendlyMessage = 'Please check your card expiration date and try again.'
                    } else if (errorMessage.includes('cardCode')) {
                      userFriendlyMessage = 'Please check your card security code (CVV) and try again.'
                    } else {
                      userFriendlyMessage = `Payment failed A: ${errorMessage}`
                    }

                    dispatchSweetAlert({
                      alertMessage: userFriendlyMessage,
                      showCancel: false
                    })
                  } else if (
                    response.error ||
                    response.errors ||
                    response.status === 'error' ||
                    response.status === 'failed'
                  ) {
                    // Handle other error response formats
                    const errorMessage = response.error || response.errors || response.message || 'Unknown error'
                    dispatchSweetAlert({
                      alertMessage: `Payment failed B: ${errorMessage}`,
                      showCancel: false
                    })
                  } else {
                    dispatchSweetAlert({
                      alertMessage: 'Payment processing failed 1: No payment token received',
                      showCancel: false
                    })
                  }
                }
              })
            } catch (error1) {
              // Check if this is a validation error
              if (error1.message && error1.message.includes('validation')) {
                dispatchSweetAlert({
                  alertMessage: 'Card validation failed. Please check your card information and try again.',
                  showCancel: false
                })
              }

              try {
                // Method 2: Try with cardData first
                if (!cardData || Object.keys(cardData).length === 0) {
                  throw new Error('cardData is empty')
                }

                // Add timeout to catch when Authorize.net doesn't respond
                // This will only fire if NO response is received (success or error)
                let responseReceived2 = false
                const timeoutId2 = setTimeout(() => {
                  if (!responseReceived2) {
                    // Payment validation timeout
                  }
                }, 10000) // 10 second timeout

                window.Accept.dispatchData(cardData, authData, function (response) {
                  responseReceived2 = true
                  clearTimeout(timeoutId2)

                  if (response.opaqueData) {
                    // Store the real payment nonce from Authorize.net
                    setPaymentData({
                      dataValue: response.opaqueData.dataValue,
                      dataDescriptor: response.opaqueData.dataDescriptor
                    })

                    // Update the payment method to indicate hosted form was used
                    if (setPaymentMethod) {
                      setPaymentMethod('hosted_credit_card_ui')
                    }

                    // Process the payment with the real nonce
                    processPaymentWithNonce(response.opaqueData)

                    // Close modal
                    document.body.removeChild(modal)
                  } else {
                    // Check if response has a different structure
                    if (response.messages && response.messages.resultCode === 'Error') {
                      const errorMessage = response.messages.message?.[0]?.text || 'Unknown error'

                      // Enhanced error handling for common Authorize.net issues
                      let userFriendlyMessage = 'Payment failed C. Please try again.'

                      if (errorMessage.includes('cardNumber')) {
                        if (errorMessage.includes('MaxLength')) {
                          userFriendlyMessage =
                            'Invalid card number format. Please enter your card number without spaces or dashes.'
                        } else if (errorMessage.includes('cardNumber')) {
                          userFriendlyMessage = 'Please check your card number and try again.'
                        }
                      } else if (errorMessage.includes('expirationDate')) {
                        userFriendlyMessage = 'Please check your card expiration date and try again.'
                      } else if (errorMessage.includes('cardCode')) {
                        userFriendlyMessage = 'Please check your card security code (CVV) and try again.'
                      } else {
                        userFriendlyMessage = `Payment failed D: ${errorMessage}`
                      }

                      dispatchSweetAlert({
                        alertMessage: userFriendlyMessage,
                        showCancel: false
                      })
                    } else if (
                      response.error ||
                      response.errors ||
                      response.status === 'error' ||
                      response.status === 'failed'
                    ) {
                      // Handle other error response formats
                      const errorMessage = response.error || response.errors || response.message || 'Unknown error'
                      dispatchSweetAlert({
                        alertMessage: `Payment failed E: ${errorMessage}`,
                        showCancel: false
                      })
                    } else {
                      dispatchSweetAlert({
                        alertMessage: 'Payment processing failed 2: No payment token received',
                        showCancel: false
                      })
                    }
                  }
                })
              } catch (error2) {
                // Check if this is a validation error
                if (error2.message && error2.message.includes('validation')) {
                  dispatchSweetAlert({
                    alertMessage: 'Card validation failed. Please check your card information and try again.',
                    showCancel: false
                  })
                }

                try {
                  // Method 3: Try with a single object parameter
                  const combinedData = {
                    authData,
                    cardData
                  }

                  // Add timeout to catch when Authorize.net doesn't respond
                  // This will only fire if NO response is received (success or error)
                  let responseReceived3 = false
                  const timeoutId3 = setTimeout(() => {
                    if (!responseReceived3) {
                      // Payment validation timeout
                    }
                  }, 10000) // 10 second timeout

                  window.Accept.dispatchData(combinedData, function (response) {
                    responseReceived3 = true
                    clearTimeout(timeoutId3)

                    if (response.opaqueData) {
                      // Store the real payment nonce from Authorize.net
                      setPaymentData({
                        dataValue: response.opaqueData.dataValue,
                        dataDescriptor: response.opaqueData.dataDescriptor
                      })

                      // Update the payment method to indicate hosted form was used
                      if (setPaymentMethod) {
                        setPaymentMethod('hosted_credit_card_ui')
                      }

                      // Process the payment with the real nonce
                      processPaymentWithNonce(response.opaqueData)

                      // Close modal
                      document.body.removeChild(modal)
                    } else {
                      // Check if response has a different structure
                      if (response.messages && response.messages.resultCode === 'Error') {
                        const errorMessage = response.messages.message?.[0]?.text || 'Unknown error'

                        // Enhanced error handling for common Authorize.net issues
                        let userFriendlyMessage = 'Payment failed F. Please try again.'

                        if (errorMessage.includes('MaxLength')) {
                          userFriendlyMessage =
                            'Invalid card number format. Please enter your card number without spaces or dashes.'
                        } else if (errorMessage.includes('cardNumber')) {
                          userFriendlyMessage = 'Please check your card number and try again.'
                        } else if (errorMessage.includes('expirationDate')) {
                          userFriendlyMessage = 'Please check your card expiration date and try again.'
                        } else if (errorMessage.includes('cardCode')) {
                          userFriendlyMessage = 'Please check your card security code (CVV) and try again.'
                        } else {
                          userFriendlyMessage = `Payment failed G: ${errorMessage}`
                        }

                        dispatchSweetAlert({
                          alertMessage: userFriendlyMessage,
                          showCancel: false
                        })
                      } else if (
                        response.error ||
                        response.errors ||
                        response.status === 'error' ||
                        response.status === 'failed'
                      ) {
                        // Handle other error response formats
                        const errorMessage = response.error || response.errors || response.message || 'Unknown error'
                        dispatchSweetAlert({
                          alertMessage: `Payment failed H: ${errorMessage}`,
                          showCancel: false
                        })
                      } else {
                        dispatchSweetAlert({
                          alertMessage: 'Payment processing failed 3: No payment token received',
                          showCancel: false
                        })
                      }
                    }
                  })
                } catch (error3) {
                  // Check if this is a validation error
                  if (error3.message && error3.message.includes('validation')) {
                    dispatchSweetAlert({
                      alertMessage: 'Card validation failed. Please check your card information and try again.',
                      showCancel: false
                    })
                  } else {
                    dispatchSweetAlert({
                      alertMessage: `Payment processing error (12): ${error3.message}`,
                      showCancel: false
                    })
                  }
                }
              }
            }
          } else {
            dispatchSweetAlert({
              alertMessage: 'Payment system not available. Please try again or use the inline form.',
              showCancel: false
            })
          }
        }

        // Close button
        const closeButton = document.createElement('button')
        closeButton.type = 'button'
        closeButton.textContent = 'Cancel'
        closeButton.style.cssText =
          'width: 100%; padding: 10px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin-top: 10px;'
        closeButton.onclick = function () {
          setWasCancelled(true) // ✅ Prevent auto-reopening

          // ✅ Close the parent CheckoutConfirm dialog
          if (hideModal) {
            hideModal()
          }

          document.body.removeChild(modal)
        }

        // Assemble form
        form.appendChild(cardNumberInput)
        form.appendChild(expMonthInput)
        form.appendChild(expYearInput)
        form.appendChild(cardCodeInput)
        form.appendChild(submitButton)
        form.appendChild(closeButton)

        modal.appendChild(form)
        document.body.appendChild(modal)

        // Focus on the card number field after the modal is added to the DOM
        setTimeout(() => {
          const cardNumberField = document.getElementById('hostedCardNumber')
          if (cardNumberField) {
            cardNumberField.focus()
          }
        }, 100) // Small delay to ensure the modal is fully rendered
      }
    } catch (error) {
      dispatchSweetAlert({
        alertMessage: `Payment processing failed 4: ${error.message}`,
        showCancel: false
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const processPaymentWithNonce = async opaqueData => {
    try {
      // OPTION A IMPLEMENTATION - COMPLETE ✅
      // IMPLEMENTED: Now including full order data to match /orders/validate structure
      // ENHANCED: Full order data from orderData (spread first)
      // ENHANCED ERROR HANDLING: Better detection of Authorize.net error responses
      // TIMEOUT RESTORED: Re-enabled to fix error message functionality

      // Helper function to format address data with N/A fallbacks
      const formatAddress = address => {
        if (!address) {
          return {
            name: 'N/A',
            company_name: 'N/A',
            address_line_1: 'N/A',
            address_line_2: 'N/A',
            city: 'N/A',
            state: 'N/A',
            zip_code: 'N/A',
            country: 'N/A',
            country_code: 'N/A',
            phone: 'N/A'
          }
        }

        return {
          name: address.name || address.full_name || 'N/A',
          company_name: address.company_name || 'N/A',
          address_line_1: address.address_line_1 || address.address1 || 'N/A',
          address_line_2: address.address_line_2 || address.address2 || 'N/A',
          city: address.city || 'N/A',
          state: address.state || address.region || 'N/A',
          zip_code: address.zip_code || address.zip || 'N/A',
          country: address.country || 'N/A',
          country_code: address.country_code || 'N/A',
          phone: address.phone || address.phone_number || address.address_phone || 'N/A'
        }
      }

      const paymentData = {
        // Payment-specific data
        amount,
        currency: 'USD',
        payment_method: 'hosted_form', // This will override the 'credit_card' from orderData
        opaque_data: {
          dataValue: opaqueData.dataValue,
          dataDescriptor: opaqueData.dataDescriptor
        },
        customer: {
          email: currentUser?.email || 'no@email.com',
          first_name: currentUser?.first_name || 'Guest',
          last_name: currentUser?.last_name || 'Checkout',
          user_id: currentUser?.id || null
        },
        // Add contact email if present (even if null)
        contact_email: orderData?.contact_email,
        // Add billing and shipping addresses
        billing_address: formatAddress(orderData?.billing_address),
        shipping_address: formatAddress(orderData?.customer_address),
        // Add multi-address selection flag
        multi_address_selection: orderData?.multi_address_selection,
        checkout_session_token: checkoutSessionToken
      }

      const response = await branditRails.post('/payment_gateway/process_credit_card', paymentData)
      const result = response.data

      if (result.success) {
        // Now create the order with the invoice number
        try {
          // Use the same formData structure that was used for validation
          const orderFormData = new FormData()

          // Add the invoice number from payment result
          orderFormData.append('invoice_number', result.invoice_number)
          orderFormData.append('description', 'TEST ID ')

          // Add the payment_intent_id from payment result
          orderFormData.append('payment_intent_id', result.payment_intent_id)

          // Add all the order data that was passed to this modal
          Object.keys(orderData).forEach(key => {
            if (orderData[key] !== null && orderData[key] !== undefined) {
              if (typeof orderData[key] === 'object') {
                orderFormData.append(key, JSON.stringify(orderData[key]))
              } else {
                orderFormData.append(key, orderData[key])
              }
            }
          })

          const orderResponse = await branditRails.post('/orders/create', orderFormData)
          const orderResult = orderResponse.data

          if (orderResult.status === 'success') {
            onPaymentSuccess({
              order: orderResponse.data,
              payment: {
                // Use the actual payment data from Authorize.net
                payment_method: 'authorize_net',
                payment_status: 'authorized'
                // Add any other payment details you need
              }
            })
          } else {
            throw new Error(orderResult.error || 'Order creation failed')
          }
        } catch (orderError) {
          console.error('🔍 orderError:', orderError)
        }
      } else {
        throw new Error(result.error || 'Payment processing failed 5')
      }
    } catch (error) {
      // Enhanced error handling for common Authorize.net issues
      let userFriendlyMessage = 'Payment processing failed 6. Please try again.'

      if (error.message && error.message.includes('cardNumber')) {
        if (error.message.includes('MaxLength')) {
          userFriendlyMessage = 'Invalid card number format. Please enter your card number without spaces or dashes.'
        } else if (error.message.includes('cardNumber')) {
          userFriendlyMessage = 'Please check your card number and try again.'
        }
      } else if (error.message && error.message.includes('expirationDate')) {
        userFriendlyMessage = 'Please check your card expiration date and try again.'
      } else if (error.message && error.message.includes('cardCode')) {
        userFriendlyMessage = 'Please check your card security code (CVV) and try again.'
      } else if (error.message) {
        userFriendlyMessage = `Payment error 20: ${error.message}`
      }

      dispatchSweetAlert({
        alertMessage: userFriendlyMessage,
        showCancel: false
      })
    }
  }

  return (
    <>
      {/* Authorize.net Form */}
      <div className="hosted-payment-form">
        {/* Authorize.net Hosted Payment Form */}
        {!renderFormOnly && (
          <div className="hosted-form-container">
            <button
              type="button"
              onClick={handleHostedPayment}
              disabled={isProcessing}
              className="btn btn-primary btn-lg"
              style={{ width: '100%', padding: '15px' }}
            >
              {isProcessing ? 'Processing...' : 'Pay with Authorize.net Hosted Form'}
            </button>
          </div>
        )}

        {/* Show processing message when renderFormOnly is true */}
        {renderFormOnly && isProcessing && (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading payment form...</span>
            </div>
            <p className="mt-2">Loading secure payment form...</p>
          </div>
        )}

        {/* Payment Session Display */}
        {paymentData.dataValue && (
          <div className="mt-3 p-3 bg-light border rounded">
            <small className="text-muted">
              <strong>Payment Nonce Received:</strong>
              <br />
              Data Value: {paymentData.dataValue.substring(0, 50)}...
              <br />
              Data Descriptor: {paymentData.dataDescriptor}
            </small>
          </div>
        )}

        {/* Information about hosted form */}
        {/* <div className="mt-3 p-3 bg-info text-white rounded">
          <small>
            <strong>Authorize.net Hosted Payment Form:</strong><br/>
            • Secure payment form hosted by Authorize.net<br/>
            • No credit card data stored on this site<br/>
            • PCI compliant payment processing<br/>
            • Real payment tokens for secure transactions
          </small>
        </div> */}
      </div>
    </>
  )
}

const mapStateToProps = state => {
  return {
    youPay: state.checkout.youPay,
    currentUser: state.currentUser,
    checkoutSessionToken: state.checkoutSessionToken.token
  }
}

AuthorizeNetCheckoutUIModal.propTypes = {
  amount: PropTypes.number.isRequired,
  currentUser: PropTypes.object.isRequired,
  paymentGatewayConfig: PropTypes.object.isRequired,
  orderData: PropTypes.object.isRequired, // ✅ Add here
  renderFormOnly: PropTypes.bool, // ✅ Add here
  hideModal: PropTypes.func, // ✅ Add here
  onPaymentSuccess: PropTypes.func,
  onPaymentError: PropTypes.func,
  setPaymentMethod: PropTypes.func,
  dispatchSweetAlert: PropTypes.func.isRequired,
  checkoutSessionToken: PropTypes.string.isRequired,
  // OPTION A: New props needed for full order data
  youPay: PropTypes.number // Amount to pay (already exists but documenting)
}

export default connect(mapStateToProps, {
  dispatchSweetAlert
})(AuthorizeNetCheckoutUIModal)
